import { TrueType } from 'library/ts/utilities/modules/enums/TrueType';
import * as NumberModule from 'library/ts/utilities/modules/Number';

/**
 * @description Generates UUID
 * @param pattern {String} i.e. 'xxxy-xyyx' if not set makes UUID based on default 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx' pattern
 * @return {String} uuid based on passed pattern or 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'
 */
export const generateUUID = ( pattern: string = 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx' ): string => {
	let d: number = new Date().getTime();
	return pattern.replace(
		/[xy]/g,
		( c ) => {
			const r: number = ( d + Math.random() * 16 ) % 16 | 0;
			d = Math.floor( d / 16 );
			return ( c === 'x' ? r : ( r & 0x3 | 0x8 ) ).toString( 16 );
		}
	);
};

/**
 * @description Generates and returns Player Services specific JS-safe uuid format
 * @returns {string}
 */
export const playerServicesUUID = (): string => {
	return (
		// first characters of uuid should be non-numbers
		random(
			NumberModule.random( 3, 3 ),
			alphabet().toLowerCase()
		) +
		'-' +
		generateUUID(
			random( NumberModule.random( 3, 3 ) ) +
			'-' +
			random( NumberModule.random( 3, 3 ) )
		)
	);
};

/**
 * @description Returns random string of specified length generated with a specified characters
 * @param length {Number} length of string to generate
 * @param chars {String} set of characters to use
 * @return {String}
 */
export const random = ( length: number = 10, chars: string = 'xy' ): string => {
	let output = '';
	while ( length-- ) output += chars[ Math.floor( Math.random() * chars.length ) ];
	return output;
};

/**
 * @description Shuffles string characters
 * @param {string} str
 * @return {string}
 */
export const shuffle = ( str: string = '' ) => {
	return [ ...str ].sort( () => Math.random() - .5 ).join( '' );
};

/**
 * @description Generates alphabet string
 * @return {string}
 */
export const alphabet = (): string => {
	const alphabet: string[] = [];
	const start: number = ( 'A' ).charCodeAt( 0 );
	const last: number = ( 'Z' ).charCodeAt( 0 );
	for ( let i: number = start; i <= last; ++i ) {
		alphabet.push( String.fromCharCode( i ) );
	}
	return alphabet.join( '' );
};

/**
 * @description Trims string with RegEx
 * @param x {String}
 * @return {String}
 */
export const trim = ( x: string ): string => {
	return x.replace( /^\s+|\s+$/gm, '' );
};

/**
 * @description Checks existence of substring in a sourceString
 * @param sourceString {String}
 * @param substring {String|Array}
 * @return {Boolean}
 */
export const hasSubstring = ( sourceString: string, substring: string | string[] ): boolean => {
	// convert string to array... we need array for testing substring against
	if ( typeof substring === TrueType.STRING ) substring = [ substring ] as string[];
	// sanity check
	if ( substring instanceof Array ) {
		// loop over substrings
		for ( let i = 0; i < substring.length; i++ ) {
			// on match break loop + return true
			if ( sourceString.indexOf( substring[ i ] ) !== -1 ) {
				return true; // return true
			}
		}
	}
	// otherwise return false
	return false;
};

/**
 * @description String startPad method
 * @param str {String} string to pad
 * @param paddingChar {String} padding character
 * @param totalWidth {Number} total number of characters in output string
 * @return {*}
 */
export const startPad = ( str: string = '', paddingChar: string = '0', totalWidth: number = 1 ): string => {
	if ( str.length >= totalWidth ) return str;

	const array: number[] | string[] = new Array( totalWidth );

	for ( let i = 0; i < array.length; i++ ) {
		array[ i ] = paddingChar;
	}

	return ( array.join( '' ) + str ).slice( -array.length );
};

/**
 * @description Clean string from whitespaces, newlines, carriage returns, whitespaces before / between / after tags
 * @param {string} str
 * @param {boolean} carriageReturn
 * @param {boolean} tab
 * @param {boolean} whitespaceBeforeTags
 * @param {boolean} whitespaceBetweenTags
 * @param {boolean} whitespaceAfterTags
 * @returns {string}
 */
export const cleanUp = (
	str: string,
	carriageReturn: boolean = true,
	tab: boolean = true,
	whitespaceBeforeTags: boolean = true,
	whitespaceBetweenTags: boolean = true,
	whitespaceAfterTags: boolean = true
): string => {
	// remove newline / carriage return
	if ( carriageReturn === true ) str = str.replace( /\n/g, '' );

	// remove tab
	if ( tab === true ) str = str.replace( /\t/g, '' );

	// remove whitespace (space and tabs) before tags
	if ( whitespaceBeforeTags === true ) str = str.replace( /[\t ]+\</g, '<' );

	// remove whitespace between tags
	if ( whitespaceBetweenTags === true ) str = str.replace( /\>[\t ]+\</g, '><' );

	// remove whitespace after tags
	if ( whitespaceAfterTags === true ) str = str.replace( /\>[\t ]+$/g, '>' );

	return str;
};

/**
 * @description Helper function: calculate minutes:seconds
 * @param pSeconds {Number}
 * @return {String}
 */
export const timeFormatter = ( pSeconds: number ): string => {
	if ( pSeconds < 0 || isNaN( pSeconds ) ) return '00:00';
	// get total video length
	// var tmpTime = this.roundToDecimal( pSeconds, 0 );
	const tmpTime: number = Math.round( pSeconds );
	let secondsOUT;
	let minutesOUT;
	let hoursOUT;

	let seconds = tmpTime;

	let minutes = Math.floor( seconds / 60 );
	const hours = Math.floor( minutes / 60 );

	minutes = minutes % 60;
	seconds = seconds % 60;

	hoursOUT = String( hours );
	minutesOUT = String( minutes );
	secondsOUT = String( seconds );

	if ( hours < 10 ) {
		hoursOUT = '0' + hours;
	}

	if ( minutes < 10 ) {
		minutesOUT = '0' + minutes;
	}

	if ( seconds < 10 ) {
		secondsOUT = '0' + seconds;
	}

	if ( hours !== 0 ) {
		return hoursOUT + ':' + minutesOUT + ':' + secondsOUT;
	} else {
		return minutesOUT + ':' + secondsOUT;
	}
};

/**
 * @description Escapes HTML in String
 * @param string {String}
 * @return {String}
 */
export const escapeHTML = ( string: string ): string => {
	const entityMap = {
		'&': '&amp;',
		'<': '&lt;',
		'>': '&gt;',
		'"': '&quot;',
		'\'': '&#39;',
		'/': '&#x2F;'
	};

	return String( string ).replace(
		/[&<>"'\/]/g,
		( s ) => {
			return entityMap[ s ];
		}
	);
};

/**
 * @description Advanced string replace
 * @param {string} string
 * @param {string} pattern
 * @param {string} newValue
 * @param {string} regexFlags
 * @return {string}
 */
export const replace = ( string: string = '', pattern: string, newValue: string = '', regexFlags: string = '' ): string => {
	RegExp[ 'escape' ] = ( string ) => {
		return string.replace( /[-\/\\^$*+?.()|[\]{}]/g, '\\$&' );
	};

	const regex = new RegExp( RegExp[ 'escape' ]( pattern ), regexFlags );

	return string.replace( regex, newValue );
};

/**
 * @description compare two versions
 * @param version1 {String}
 * @param version2 {String}
 * @return {Number}
 *  @example
 * // input version1 and version2 - Strings
 * const version1 = '2.7.1';
 * const version2 = '2.8';
 * console.log( compareVersion( version1, version2 ) ) // prints output: -1
 */
export const compareVersion = ( version1: string, version2: string ): number => {
	const v1 = version1.split( '.' ).map( Number );
	const v2 = version2.split( '.' ).map( Number );

	const k = Math.min( v1.length, v2.length );
	for ( let i = 0; i < k; ++i ) {
		if ( v1[ i ] > v2[ i ] ) return 1;
		if ( v1[ i ] < v2[ i ] ) return -1;
	}
	return v1.length == v2.length ? 0 : ( v1.length < v2.length ? -1 : 1 );
};